
import { Component, Prop } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import CoachEditor from '@/components/coach/planning/CoachEditor.vue';
import { Viewer } from '@toast-ui/vue-editor';
import { LessonPlan, Section } from '@/models/lessonPlan';
import InsertLink from '@/components/scheme/InsertLink.vue';
import ViewSkillModal from '@/components/coach/scheme/ViewSkillModal.vue';
import MoreButton from '@/components/scheme/MoreButton.vue';
import { SchemeScope } from '@/components/mixins/schemescope';
import { mixins } from 'vue-class-component';

@Component({
  name: 'LessonPlanContent',
  components: {
    CoachEditor,
    Viewer,
    InsertLink,
    draggable,
    ViewSkillModal,
    MoreButton,
  },
})
export default class LessonPlanContent extends mixins(SchemeScope) {
  @Prop() private section!: Section;
  @Prop() private isDeviceOnline!: boolean;
  @Prop() private pdfGenerating!: boolean;

  private editing = false;
  private showAddSkill = false;
  private showSkillModal = false;

  private selectedSkill = '';
  private skills = [] as string[];

  private editingTitle = false;
  private newTitle = '';

  created() {
    if (this.section.skills) {
      this.skills = this.section.skills;
    }
    this.newTitle = this.section.title;
  }

  get plan(): LessonPlan {
    return this.$store.getters['lessonPlans/selectedPlan'];
  }

  get readOnly(): boolean {
    return this.plan.readonly === 1;
  }

  get skillsList() {
    return this.skills.map((id: string) => {
      let title = id;
      let parentTitle = '';
      const node = this.getNode(id);
      if (node) {
        title = node.title;
        if (node.parentId && node.parentId !== '/') {
          const parentNode = this.getNode(node.parentId);
          if (parentNode) {
            parentTitle = parentNode.title;
          }
        }
      }
      return {
        id,
        title,
        parentTitle,
      };
    });
  }

  cancelEditing() {
    this.editing = false;
    this.cancelEditTitle();
  }

  showSkill(nodeId: string) {
    this.selectedSkill = nodeId;
    this.showSkillModal = true;
  }

  async save() {
    const editor = this.$refs[this.section.id] as any;
    let markdown = '';
    if (editor) {
      markdown = editor.$refs.toastuiEditor.invoke('getMarkdown');
    }
    this.section.description = markdown;
    this.section.skills = this.skills;
    try {
      await this.$store.dispatch('lessonPlans/updateSection', this.section);
      await this.$store.dispatch('lessonPlans/updateSectionSkills', this.section);
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.plan.uuid);
      this.$alert('success', this.$t('planning.content_block_updated'));
    } catch {
      this.$alert('error', this.$t('planning.content_block_not_updated'));
    }
    this.editing = false;
  }

  addSkill(schemeNodeId: string) {
    if (!this.skills.includes(schemeNodeId)) {
      this.skills.push(schemeNodeId);
    }
    this.showAddSkill = false;
  }

  removeSkill(id: string) {
    this.skills = this.skills.filter(v => v !== id);
  }

  async edit() {
    if (this.readOnly) {
      const res = await this.$confirm(this.$t('planning.confirm_duplicate') as string, {
        title: this.$t('planning.duplicate_session') as string,
        color: 'dark',
        icon: 'mdi-help-circle-outline',
        buttonTrueText: this.$t('global.yes') as string,
        buttonFalseText: this.$t('global.no') as string,
      });
      if (res) {
        this.$emit('duplicate');
      }
    } else {
      this.editing = true;
    }
  }

  async updateTitle() {
    try {
      this.section.title = this.newTitle;
      await this.$store.dispatch('lessonPlans/updateSection', this.section);
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.plan.uuid); // Update plan in db
      await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.plan.uuid); // Fetch updated plan
      this.editingTitle = false;
      this.$alert('success', this.$t('planning.title_updated'));
    } catch (error) {
      this.editingTitle = false;
      this.$alert('error', this.$t('planning.title_not_updated'));
    }
  }

  cancelEditTitle() {
    this.editingTitle = false;
    this.newTitle = this.section.title;
  }

  async renameSection() {
    this.editingTitle = true;
  }

  async duplicateSection() {
    try {
      await this.$store.dispatch('lessonPlans/duplicateSection', this.section);
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.plan.uuid);
      await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.plan.uuid);
      this.$alert('success', this.$t('planning.content_block_duplicated'));
    } catch (error) {
      this.$alert('error', this.$t('planning.content_block_not_duplicated'));
    }
  }

  async deleteSection() {
    try {
      await this.$store.dispatch('lessonPlans/deleteSection', this.section);
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.plan.uuid);
      await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.plan.uuid);
      this.$alert('success', this.$t('planning.content_block_deleted'));
    } catch (error) {
      this.$alert('error', this.$t('planning.content_block_not_deleted'));
    }
  }

  get showContent() {
    return this.$store.getters['auth/isFeatureEnabled']('ENABLE_LESSON_PLAN_CONTENT');
  }

  get canAddSession() {
    return this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN');
  }

  get canEditSession() {
    return this.canAddSession || this.$store.getters['auth/isFeatureEnabled']('CAN_EDIT_SESSION_PLAN');
  }

  get canDeleteSession() {
    return this.canAddSession || this.$store.getters['auth/isFeatureEnabled']('CAN_DELETE_SESSION_PLAN');
  }
}
